import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  HR_FORECAST_CATEGORIES,
  HR_RECOMMENDATION_PRIORITIES,
  HR_RECOMMENDATION_STATUSES,
  HrAiContext,
  HrAnalyticsDashboard,
  HrAnalyticsExport,
  HrAnalyticsSnapshot,
  HrWorkforceForecastScenario,
  HrWorkforceOptimizationRecommendation
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface SelectOption<T = string | number> {
  label: string;
  value: T;
}

@Component({
  selector: 'app-hr-analytics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-analytics.component.html',
  styleUrls: ['./hr-analytics.component.scss']
})
export class HrAnalyticsComponent implements OnInit {
  activeListTab = 'snapshots';
  activeDialog?: 'snapshot' | 'forecast' | 'recommendation';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  readonly canManage = this.permissionService.hasPermission(app_permissions.hr.analytics.manage);
  readonly canExport = this.permissionService.hasPermission(app_permissions.hr.analytics.export);
  readonly canViewAi = this.permissionService.hasPermission(app_permissions.hr.analytics.aiView);

  readonly forecastCategoryOptions: SelectOption[] = HR_FORECAST_CATEGORIES.map(value => ({ label: value.replaceAll('_', ' '), value }));
  readonly priorityOptions: SelectOption[] = HR_RECOMMENDATION_PRIORITIES.map(value => ({ label: value, value }));
  readonly statusOptions: SelectOption[] = HR_RECOMMENDATION_STATUSES.map(value => ({ label: value, value }));

  dashboard?: HrAnalyticsDashboard;
  snapshots: HrAnalyticsSnapshot[] = [];
  forecasts: HrWorkforceForecastScenario[] = [];
  recommendations: HrWorkforceOptimizationRecommendation[] = [];
  aiContext?: HrAiContext;
  exportPreview?: HrAnalyticsExport;

  snapshotDate = new Date().toISOString().slice(0, 10);
  forecastForm: HrWorkforceForecastScenario = {
    scenarioStatus: 'DRAFT',
    forecastCategory: 'WORKFORCE',
    periodStart: new Date().toISOString().slice(0, 10),
    periodEnd: new Date().toISOString().slice(0, 10),
    requiredHeadcount: 0,
    availableHeadcount: 0
  };
  recommendationForm: HrWorkforceOptimizationRecommendation = {
    recommendationStatus: 'OPEN',
    priority: 'MEDIUM',
    recommendationType: 'WORKFORCE_OPTIMIZATION',
    humanApprovalRequired: true
  };

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Total Employees', value: this.dashboard?.totalEmployees || 0 },
      { label: 'Active Employees', value: this.dashboard?.activeEmployees || 0, tone: 'success' },
      { label: 'Snapshots', value: this.dashboard?.analyticsSnapshots || 0, tone: 'info' },
      { label: 'Forecasts', value: this.dashboard?.forecastScenarios || 0 },
      { label: 'Open Recommendations', value: this.dashboard?.openRecommendations || 0, tone: 'warn' }
    ];
  }

  load(): void {
    forkJoin({
      dashboard: this.hrService.analyticsDashboard(),
      snapshots: this.hrService.analyticsSnapshots(),
      forecasts: this.hrService.workforceForecasts(),
      recommendations: this.hrService.workforceRecommendations(undefined, 'OPEN'),
      aiContext: this.canViewAi ? this.hrService.hrAiContext() : of({ data: undefined } as any)
    }).subscribe({
      next: result => {
        this.dashboard = result.dashboard.data;
        this.snapshots = result.snapshots.data || [];
        this.forecasts = result.forecasts.data || [];
        this.recommendations = result.recommendations.data || [];
        this.aiContext = this.canViewAi ? result.aiContext.data : undefined;
      },
      error: () => this.toast('error', 'Unable to load HR analytics.')
    });
  }

  aggregate(): void {
    if (!this.canManage) return;
    this.hrService.aggregateAnalytics(undefined, this.snapshotDate).subscribe({
      next: () => {
        this.toast('success', 'Analytics snapshots generated.');
        this.activeDialog = undefined;
        this.load();
      },
      error: () => this.toast('error', 'Unable to generate analytics snapshots.')
    });
  }

  saveForecast(): void {
    if (!this.canManage) return;
    this.hrService.saveWorkforceForecast(this.forecastForm).subscribe({
      next: () => {
        this.forecastForm = {
          scenarioStatus: 'DRAFT',
          forecastCategory: 'WORKFORCE',
          requiredHeadcount: 0,
          availableHeadcount: 0
        };
        this.activeDialog = undefined;
        this.toast('success', 'Forecast scenario saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save forecast scenario.')
    });
  }

  saveRecommendation(): void {
    if (!this.canManage) return;
    this.hrService.saveWorkforceRecommendation(this.recommendationForm).subscribe({
      next: () => {
        this.recommendationForm = {
          recommendationStatus: 'OPEN',
          priority: 'MEDIUM',
          recommendationType: 'WORKFORCE_OPTIMIZATION',
          humanApprovalRequired: true
        };
        this.activeDialog = undefined;
        this.toast('success', 'Recommendation saved.');
        this.load();
      },
      error: () => this.toast('error', 'Unable to save recommendation.')
    });
  }

  prepareExport(): void {
    if (!this.canExport) return;
    this.hrService.analyticsExport(undefined, 'JSON').subscribe({
      next: result => {
        this.exportPreview = result.data;
        this.toast('success', 'Analytics export prepared.');
      },
      error: () => this.toast('error', 'Unable to prepare analytics export.')
    });
  }

  openActionDialog(dialog: NonNullable<HrAnalyticsComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrAnalyticsComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrAnalyticsComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  severity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch ((value || '').toUpperCase()) {
      case 'LOW':
      case 'ACTIVE':
      case 'APPROVED':
        return 'success';
      case 'HIGH':
      case 'OPEN':
        return 'danger';
      case 'MEDIUM':
      case 'DRAFT':
        return 'warn';
      default:
        return 'info';
    }
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: severity === 'success' ? 'Saved' : 'Error', detail });
  }
}
